import { useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { message, Modal } from "antd";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { REASON_MODAL_CONFIG, TITLE_TXT, FORM_MODAL_WIDTHS, CREDIT_LIMIT_MSGS, STATUS } from "../../../constants/constants";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import {
    rejectBusEnt,
    deativateBusEnt,
    reopenBusEnt,
    activateBusEnt,
    getBusEnt,
} from "../../../redux/slices/creditLimitSlice";
import { getCreditLimitColumns } from "./getCreditLimitColumns";
import ApproveEntBus from "../Components/ApproveEntBus";
import EnhanceEntBus from "../Components/EnhanceEntBus";
import { useNavigate } from "react-router-dom";
import { count } from "../../../redux/slices/dealsSlice";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";

const isBusinessDeactivatedOrDelinquent = (record) => {
    if (!record) return false;
    const statusId = record.businessStatusId;
    const statusName = (record.businessStatusName || "").toUpperCase();
    return (
        statusId === STATUS.DEACTIVATED.ID ||
        statusId === STATUS.DELINQUENT.ID ||
        statusName === STATUS.DEACTIVATED.NAME ||
        statusName === STATUS.DELINQUENT.NAME
    );
};

const CreditLimitTable = ({ tableData, selectedStatus, onColumnControlChange }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { commentSubmitError, commentSubmitLoading } = useSelector(
        (state) => state?.creditLimit
    );
    const [modal, setModal] = useState({ type: null, data: null });

    const userPrivileges = useMemo(() => {
        const raw = user?.privilege || [];
        return raw
            .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
            .filter(Boolean);
    }, [user]);

    const closeModal = useCallback(() => setModal({ type: null, data: null }), []);

    const openModal = useCallback((type, record) => setModal({ type, data: record }), []);

    const handleReactivateClick = useCallback(
        (record) => {
            if (isBusinessDeactivatedOrDelinquent(record)) {
                Modal.warning({
                    title: CREDIT_LIMIT_MSGS.BUSINESS_MUST_BE_ACTIVE_TITLE || "Cannot Reactivate Credit Limit",
                    content: CREDIT_LIMIT_MSGS.BUSINESS_MUST_BE_ACTIVE,
                    centered: true,
                });
                return;
            }

            openModal("activate", record);
        },
        [openModal]
    );

    const handleReasonSubmit = useCallback(
        async (values) => {
            if (!modal.type || !modal.data?.busEntId) return;

            let action;
            switch (modal.type) {
                case "reject":
                    action = rejectBusEnt;
                    break;
                case "deactivate":
                    action = deativateBusEnt;
                    break;
                case "reopen":
                    action = reopenBusEnt;
                    break;
                case "activate":
                    action = activateBusEnt;
                    break;
                default:
                    break;
            }

            if (action) {
                const res = await dispatch(
                    action({
                        busEntId: modal.data.busEntId,
                        comments: values.reason,
                        modifiedAt: modal.data.modifiedAt,
                    })
                );

                const businessName = modal.data?.orgName || "Business";
                const enterpriseName = modal.data?.entName || "Enterprise";
                const actionLabels = {
                    reject: "Rejected",
                    deactivate: "Deactivated",
                    reopen: "Reopened",
                    activate: "Reactivated",
                };
                const actionLabel = actionLabels[modal.type] || "Processed";

                if (res?.meta?.requestStatus === "fulfilled" && res.payload?.statusCode === 201) {
                    message.success(
                        CREDIT_LIMIT_MSGS.ACTION_SUCCESS.replace("{business}", businessName).replace(
                            "{enterprise}",
                            enterpriseName,
                        ).replace("{actionLabel}", actionLabel),
                    );
                    dispatch(getBusEnt(selectedStatus));
                    dispatch(count("CREDITLIMIT"));
                    closeModal();
                    return;
                }

                const errorMessage =
                    (res?.payload &&
                        (res.payload.message || res.payload.error || String(res.payload))) ||
                    (res?.error &&
                        (res.error.message || String(res.error))) ||
                    CREDIT_LIMIT_MSGS.ACTION_FAILED.replace("{action}", actionLabel).replace(
                        "{business}",
                        businessName,
                    ).replace("{enterprise}", enterpriseName);

                if (
                    typeof errorMessage === "string" &&
                    (errorMessage.includes("Active status before the credit limit can be activated") ||
                        errorMessage.includes("must be in Active status") ||
                        errorMessage.includes(CREDIT_LIMIT_MSGS.BUSINESS_MUST_BE_ACTIVE) ||
                        errorMessage.includes("Active before the credit limit can be activated"))
                ) {
                    closeModal();
                    Modal.warning({
                        title: CREDIT_LIMIT_MSGS.BUSINESS_MUST_BE_ACTIVE_TITLE || "Cannot Reactivate Credit Limit",
                        content: CREDIT_LIMIT_MSGS.BUSINESS_MUST_BE_ACTIVE,
                        centered: true,
                    });
                    return;
                }

                message.error(errorMessage);
            }
        },
        [dispatch, modal, closeModal, selectedStatus]
    );

    const columns = useMemo(
        () =>
            getCreditLimitColumns({
                onView: (id, record) => {
                    const rowStatus =
                        record?.busEntStatusId ||
                        record?.statusId ||
                        (Array.isArray(selectedStatus) ? selectedStatus[0] : selectedStatus);
                    navigate(`/credit-limit/BusEnt-details/${id}/${rowStatus}`);
                },
                onApprove: (record) => openModal("approve", record),
                onReject: (record) => openModal("reject", record),
                onDeactivate: (record) => openModal("deactivate", record),
                onEnhance: (record) => openModal("enhance", record),
                onReopen: (record) => openModal("reopen", record),
                onReactivate: (record) => handleReactivateClick(record),
                status: selectedStatus,
                privileges: userPrivileges,
            }),
        [navigate, openModal, selectedStatus, userPrivileges, handleReactivateClick]
    );

    return (
        <>
            <CustomTable
                rowKey="busEntId"
                columns={columns}
                dataSource={tableData}
                screenKey={buildTableScreenKey("creditLimit")}
                defaultColumnKeys={TABLE_COLUMN_DEFAULTS.creditLimit}
                onColumnControlChange={onColumnControlChange}
            />

            {/* Reason Modals */}
            {modal.type && ["reject", "deactivate", "reopen", "activate"].includes(modal.type) && (
                <CustomModal
                    title={REASON_MODAL_CONFIG[modal.type]?.title}
                    open
                    centered
                    content={
                        <ReasonForm
                            label={REASON_MODAL_CONFIG[modal.type]?.label}
                            placeholder="Write a valid reason"
                            btnName={REASON_MODAL_CONFIG[modal.type]?.btnName}
                            btnType={["deactivate", "reject"].includes(modal.type) ? "danger" : "primary"}
                            submitLoading={commentSubmitLoading}
                            submitError={commentSubmitError}
                            onClose={closeModal}
                            handleOnFormSubmit={handleReasonSubmit}
                        />
                    }
                />
            )}

            {/* Approve Modal */}
            {modal.type === "approve" && (
                <CustomModal
                    title={TITLE_TXT.APPROVE_ENT_BUS}
                    open
                    centered
                    width={FORM_MODAL_WIDTHS}
                    content={
                        <ApproveEntBus
                            data={modal.data}
                            onClose={closeModal}
                            status={selectedStatus}
                        />
                    }
                />
            )}

            {modal.type === "enhance" && (
                <CustomModal
                    title="Enhance Business Enterprise"
                    open
                    centered
                    width={FORM_MODAL_WIDTHS}
                    content={
                        <EnhanceEntBus
                            data={modal.data}
                            onClose={closeModal}
                            status={selectedStatus}
                        />
                    }
                />
            )}

        </>
    );
};

export default CreditLimitTable;
